import React, { useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = window.location.origin;
// Primary API base from env or same-origin; fallback to same-origin '/api' only if primary fails
const API = `${BACKEND_URL}/api`;
const API_FALLBACK = `/api`; // Same-origin (ingress routes '/api' to backend)

const SubscriptionPage = ({ onBack, userToken }) => {
  // Subscription state
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState('');
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);

  // Load subscription status on component mount
  useEffect(() => {
    loadSubscription();
    
    // Check for payment result in URL params
    checkPaymentResult();
  }, []);

  const loadSubscription = async () => {
    try {
      setLoading(true);
      setError(null);

      const headers = {
        'Authorization': `Bearer ${userToken}`,
        'Content-Type': 'application/json'
      };

      // Try to get detailed subscription info from Stripe endpoint first
      let response;
      try {
        response = await axios.get(`${API}/stripe/subscription-info`, { headers });
      } catch (stripeErr) {
        console.warn('Stripe subscription info failed, falling back to user subscription ...', stripeErr?.message);
        // Fallback to existing endpoint
        try {
          response = await axios.get(`${API}/user/subscription`, { headers });
        } catch (primaryErr) {
          console.warn('Primary subscription fetch failed, trying fallback /api ...', primaryErr?.message || primaryErr);
          response = await axios.get(`${API_FALLBACK}/user/subscription`, { headers });
        }
      }
      
      if (response?.data) {
        setSubscription(response.data);
      }

      setLoading(false);
    } catch (error) {
      console.error('Error loading subscription:', error);
      setError('Failed to load subscription information. Please try again.');
      setLoading(false);
    }
  };

  const checkPaymentResult = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    const paymentResult = urlParams.get('payment');
    const onSuccessPath = window.location.pathname.startsWith('/subscription/success');
    const onCancelPath = window.location.pathname.startsWith('/subscription/cancel');

    // Stripe's success redirect is /subscription/success?session_id=... with
    // NO `payment` param. Trigger polling on EITHER the explicit ?payment=success
    // marker (legacy / future flows) OR the canonical Stripe return path with
    // a session_id. Same for cancel.
    const isSuccessReturn = sessionId && (paymentResult === 'success' || onSuccessPath);
    const isCancelReturn = paymentResult === 'cancelled' || onCancelPath;

    // IMPORTANT: clean the URL IMMEDIATELY on any Stripe return so the long
    // /subscription/success?session_id=... doesn't follow the user across the
    // SPA (this app routes via React state, not paths — a dirty URL stays
    // glued in the address bar even when the user clicks Dashboard, How to
    // Start, etc.). The session_id is captured locally above and passed to
    // the poller, so URL cleanup is safe to do before polling starts.
    if (isSuccessReturn || isCancelReturn) {
      window.history.replaceState({}, document.title, '/subscription');
    }

    if (isSuccessReturn) {
      pollPaymentStatus(sessionId);
    } else if (isCancelReturn) {
      setPaymentStatus('Payment was cancelled. You can try again anytime.');
    }
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 30; // 60 seconds total
    const pollInterval = 2000; // 2 seconds

    if (attempts >= maxAttempts) {
      setPaymentStatus('Payment verification taking longer than expected. Please wait or refresh shortly.');
      // Try to reload subscription anyway and exit
      await loadSubscription();
      return;
    }

    const headers = {
      'Authorization': `Bearer ${userToken}`,
      'Content-Type': 'application/json'
    };

    try {
      // Use NEW recurring subscription status endpoint
      let response;
      try {
        response = await axios.get(`${API}/stripe/subscription-status/${sessionId}`, { headers });
      } catch (primaryErr) {
        console.warn('Primary status check failed, trying fallback /api ...', primaryErr?.message || primaryErr);
        response = await axios.get(`${API_FALLBACK}/stripe/subscription-status/${sessionId}`, { headers });
      }

      if (response?.data?.status === 'complete') {
        setPaymentStatus('🎉 Payment successful! Your subscription is now active and will automatically renew each month.');
        await loadSubscription();
        // Strip session_id from URL and land cleanly on /subscription
        window.history.replaceState({}, document.title, '/subscription');
        return;
      }
      if (response?.data?.status === 'expired') {
        setPaymentStatus('Payment session expired. Please try again.');
        return;
      }

      // If pending, continue polling
      setPaymentStatus(`Verifying payment... (${attempts + 1}/${maxAttempts})`);
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
      return;
    } catch (err1) {
      console.warn('Primary payment status check threw:', err1?.response?.status || err1?.message || err1);
      
      // Fallback: trust subscription state if already active
      try {
        await loadSubscription();
        if (subscription?.has_active_subscription) {
          setPaymentStatus('🎉 Payment successful! Your subscription is now active.');
          window.history.replaceState({}, document.title, '/subscription');
          return;
        }
      } catch (e) {
        // ignore
      }

      // Final: keep user informed but do not block usage if already active on reload
      setPaymentStatus('Payment verified with Stripe, but status check failed. Please reload the page to see your active subscription.');
    }
  };

  const handleSubscribe = async () => {
    try {
      setPaymentProcessing(true);
      setError(null);
      setPaymentStatus('');

      const headers = {
        'Authorization': `Bearer ${userToken}`,
        'Content-Type': 'application/json'
      };

      // Create RECURRING subscription checkout session
      // Uses new Stripe Subscriptions API endpoint
      let response;
      try {
        response = await axios.post(`${API}/stripe/create-subscription-checkout`, {
          origin_url: window.location.origin  // Dynamic origin for Stripe callbacks
        }, { headers });
      } catch (primaryErr) {
        console.error('Primary backend checkout failed, trying fallback /api ...', primaryErr);
        // Fallback: same-origin ingress '/api' (prevents wrong-host errors)
        response = await axios.post(`${API_FALLBACK}/stripe/create-subscription-checkout`, {
          origin_url: window.location.origin
        }, { headers });
      }

      // Redirect to Stripe Checkout
      if (response?.data?.url) {
        window.location.href = response.data.url;
      } else {
        throw new Error('No checkout URL received');
      }

    } catch (error) {
      console.error('Error creating checkout session:', error);
      const detail = error.response?.data?.detail;
      if (error.response?.status === 403 && detail === 'capacity_full') {
        setError("We've hit our paid-user capacity right now. Head to the home page to join the waitlist — we'll notify you the moment a seat opens up.");
      } else if (error.response?.status === 503) {
        setError('Payment service is currently unavailable. Please try again later.');
      } else {
        setError('Failed to initiate payment. Please try again.');
      }
      setPaymentProcessing(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const handleCancelClick = () => {
    setShowCancelConfirm(true);
  };

  const handleCancelConfirm = async () => {
    setShowCancelConfirm(false);
    await handleCancelSubscription();
  };

  const handleReactivate = async () => {
    setCancelLoading(true);
    setError(null);

    try {
      const headers = {
        'Authorization': `Bearer ${userToken}`,
        'Content-Type': 'application/json'
      };

      // Use Stripe subscription reactivation endpoint
      let response;
      try {
        response = await axios.post(`${API}/stripe/reactivate-subscription`, {}, { headers });
      } catch (primaryErr) {
        console.warn('Primary reactivate subscription failed, trying fallback /api ...', primaryErr?.message || primaryErr);
        response = await axios.post(`${API_FALLBACK}/stripe/reactivate-subscription`, {}, { headers });
      }

      if (response.data) {
        // Reload subscription to show updated status
        await loadSubscription();
        setPaymentStatus('Subscription reactivated! Your subscription will continue to auto-renew each month.');
      }

    } catch (error) {
      console.error('Error reactivating subscription:', error);
      if (error.response?.data?.detail) {
        setError(error.response.data.detail);
      } else {
        setError('Failed to reactivate subscription. Please contact support if this issue persists.');
      }
    } finally {
      setCancelLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    setCancelLoading(true);
    setError(null);

    try {
      const headers = {
        'Authorization': `Bearer ${userToken}`,
        'Content-Type': 'application/json'
      };

      // Use NEW Stripe subscription cancellation endpoint
      let response;
      try {
        response = await axios.post(`${API}/stripe/cancel-subscription`, {}, { headers });
      } catch (primaryErr) {
        console.warn('Primary cancel subscription failed, trying fallback /api ...', primaryErr?.message || primaryErr);
        response = await axios.post(`${API_FALLBACK}/stripe/cancel-subscription`, {}, { headers });
      }

      if (response.data) {
        // Reload subscription to show updated status
        await loadSubscription();
        const accessUntil = response.data.access_until ? new Date(response.data.access_until).toLocaleDateString() : 'the end of your billing period';
        setPaymentStatus(`Subscription cancelled successfully. You will retain access until ${accessUntil}. Your subscription will not automatically renew.`);
      }

    } catch (error) {
      console.error('Error cancelling subscription:', error);
      setError('Failed to cancel subscription. Please contact support if this issue persists.');
    } finally {
      setCancelLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500 mx-auto"></div>
          <h2 className="text-xl mt-4">Loading Subscription...</h2>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      {/* Header */}
      <div className="bg-gray-800 p-6 border-b border-gray-700">
        <div className="max-w-4xl mx-auto">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold mb-2">Subscription</h1>
              <p className="text-gray-400">Manage your actuvo Dynamic Grid subscription</p>
            </div>
            <button
              onClick={onBack}
              className="bg-gray-700 hover:bg-gray-600 px-4 py-2 rounded font-semibold transition-colors"
              data-testid="subscription-back-button"
            >
              ← Back to Dashboard
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto p-6">
        {/* Error Messages */}
        {error && (
          <div className="bg-red-900/20 border border-red-500 text-red-400 px-4 py-3 rounded mb-6">
            <div className="flex items-center">
              <span className="text-red-500 mr-2">⚠️</span>
              {error}
            </div>
          </div>
        )}

        {/* Payment Status Messages */}
        {paymentStatus && (
          <div className="bg-blue-900/20 border border-blue-500 text-blue-400 px-4 py-3 rounded mb-6">
            <div className="flex items-center">
              <span className="text-blue-500 mr-2">ℹ️</span>
              {paymentStatus}
            </div>
          </div>
        )}

        <div className="space-y-8">
          {/* Current Subscription Status */}
          <div className="bg-gray-800 rounded-lg p-6">
            <h2 className="text-2xl font-semibold mb-4 flex items-center">
              <span className="text-green-400 mr-3">💳</span>
              Current Subscription
            </h2>

            {subscription?.has_active_subscription ? (
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <span className="text-green-500 text-2xl">✅</span>
                  <div>
                    <h3 className="text-xl font-semibold text-green-400">Active Subscription</h3>
                    <p className="text-gray-400">
                      {subscription.subscription_type === 'recurring' 
                        ? 'Auto-renews monthly — your Dynamic Grid bot is ready to use.'
                        : 'Your Dynamic Grid bot is ready to use.'}
                    </p>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                  <div className="space-y-3">
                    <div className="bg-gray-700 p-4 rounded">
                      <div className="text-sm text-gray-400">Plan</div>
                      <div className="text-lg font-semibold">
                        Monthly Subscription
                        {subscription.subscription_type === 'recurring' && (
                          <span className="ml-2 text-xs bg-green-600 text-white px-2 py-1 rounded">Auto-Renew</span>
                        )}
                      </div>
                    </div>
                    
                    <div className="bg-gray-700 p-4 rounded">
                      <div className="text-sm text-gray-400">Price</div>
                      <div className="text-lg font-semibold">$30/month</div>
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="bg-gray-700 p-4 rounded">
                      <div className="text-sm text-gray-400">
                        {subscription.subscription_cancelled 
                          ? 'Access Until' 
                          : (subscription.subscription_type === 'recurring' ? 'Next Billing Date' : 'Expires')}
                      </div>
                      <div className="text-lg font-semibold">
                        {formatDate(subscription.next_billing_date || subscription.subscription_expires_at || subscription.expires_at)}
                      </div>
                      {subscription.subscription_cancelled && (
                        <div className="text-xs text-yellow-400 mt-1">Cancelled - will not renew</div>
                      )}
                    </div>
                    
                    <div className="bg-gray-700 p-4 rounded">
                      <div className="text-sm text-gray-400">Days Remaining</div>
                      <div className={`text-lg font-semibold ${subscription.days_remaining < 7 ? 'text-yellow-400' : 'text-green-400'}`}>
                        {subscription.days_remaining} days
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-6 p-4 bg-blue-900/20 border border-blue-700 rounded">
                  <h4 className="text-blue-400 font-semibold mb-2">What&apos;s Included:</h4>
                  <ul className="text-gray-300 space-y-1">
                    <li>• Full Dynamic Grid bot access on Hyperliquid</li>
                    <li>• Maker-only orders — reduce taker fees, qualify for maker rebates where eligible</li>
                    <li>• Smart stop, hard stop, and profit-ratchet protection</li>
                    <li>• Live grid dashboard with fills and P&amp;L</li>
                    <li>• Email support</li>
                  </ul>
                </div>

                {/* Subscription Management */}
                <div className="mt-6 pt-6 border-t border-gray-700">
                  <h4 className="text-gray-300 font-semibold mb-4">Manage Subscription</h4>
                  <div className="flex space-x-4">
                    {!subscription.subscription_cancelled ? (
                      <button
                        onClick={handleCancelClick}
                        className="bg-red-600 hover:bg-red-700 px-4 py-2 rounded font-semibold transition-colors"
                        disabled={cancelLoading}
                        data-testid="subscription-cancel-button"
                      >
                        {cancelLoading ? 'Cancelling...' : 'Cancel Subscription'}
                      </button>
                    ) : (
                      <button
                        onClick={handleReactivate}
                        className="bg-green-600 hover:bg-green-700 px-4 py-2 rounded font-semibold transition-colors"
                        disabled={cancelLoading}
                        data-testid="subscription-reactivate-button"
                      >
                        {cancelLoading ? 'Processing...' : 'Reactivate Subscription'}
                      </button>
                    )}
                  </div>
                  <p className="text-gray-400 text-sm mt-2">
                    {subscription.subscription_cancelled 
                      ? `Your subscription is cancelled but you'll retain access until ${formatDate(subscription.subscription_expires_at || subscription.expires_at)}. Reactivate to continue auto-renewal.`
                      : `Cancel anytime. You'll retain access until ${formatDate(subscription.next_billing_date || subscription.subscription_expires_at || subscription.expires_at)}`}
                  </p>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <span className="text-red-500 text-2xl">❌</span>
                  <div>
                    <h3 className="text-xl font-semibold text-red-400">No Active Subscription</h3>
                    <p className="text-gray-400">Subscribe to access the actuvo Dynamic Grid bot</p>
                  </div>
                </div>

                {subscription?.last_payment_date && (
                  <div className="mt-4 p-4 bg-gray-700 rounded">
                    <div className="text-sm text-gray-400">Last Payment</div>
                    <div className="text-lg">{formatDate(subscription.last_payment_date)}</div>
                    <div className="text-sm text-gray-400">Amount: ${subscription.last_payment_amount}</div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Subscription Plans */}
          {!subscription?.has_active_subscription && (
            <div className="bg-gray-800 rounded-lg p-6">
              <h2 className="text-2xl font-semibold mb-4 flex items-center">
                <span className="text-blue-400 mr-3">📋</span>
                Subscription Plans
              </h2>

              <div className="max-w-md mx-auto">
                <div className="bg-gradient-to-br from-blue-900 to-purple-900 border border-blue-500 rounded-lg p-6 relative">
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <span className="bg-blue-500 text-white px-4 py-1 rounded-full text-sm font-semibold">
                      Popular
                    </span>
                  </div>
                  
                  <div className="text-center">
                    <h3 className="text-2xl font-bold text-white mb-2">Monthly Plan</h3>
                    <div className="mb-4">
                      <span className="text-4xl font-bold text-blue-400">$30</span>
                      <span className="text-gray-400">/month</span>
                    </div>
                    
                    <ul className="text-gray-300 space-y-2 mb-6 text-left">
                      <li className="flex items-center">
                        <span className="text-green-400 mr-2">✓</span>
                        Full Dynamic Grid bot access
                      </li>
                      <li className="flex items-center">
                        <span className="text-green-400 mr-2">✓</span>
                        Maker-only orders — reduce taker fees
                      </li>
                      <li className="flex items-center">
                        <span className="text-green-400 mr-2">✓</span>
                        Smart stop &amp; hard stop protection
                      </li>
                      <li className="flex items-center">
                        <span className="text-green-400 mr-2">✓</span>
                        Profit ratchet (locks in gains)
                      </li>
                      <li className="flex items-center">
                        <span className="text-green-400 mr-2">✓</span>
                        Live portfolio dashboard
                      </li>
                      <li className="flex items-center">
                        <span className="text-green-400 mr-2">✓</span>
                        Email support
                      </li>
                    </ul>

                    <div className="bg-yellow-900/30 border border-yellow-600/50 rounded-lg p-3 mb-4 text-left">
                      <p className="text-yellow-100 text-xs leading-relaxed">
                        <strong>⚠️ Risk warning.</strong> Crypto perpetuals are high risk. You can lose
                        all of your deposited Hyperliquid margin, and losses can occur quickly. actuvo
                        is automation software, <strong>not financial advice</strong>, and does not
                        guarantee any profit. By subscribing you confirm you have read the
                        Risk Disclaimer and Terms, and that you alone are responsible for the funds
                        you allocate to the bot.
                      </p>
                    </div>

                    <button
                      onClick={handleSubscribe}
                      disabled={paymentProcessing}
                      className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-800 disabled:cursor-not-allowed px-6 py-3 rounded-lg font-semibold transition-colors"
                      data-testid="subscription-subscribe-button"
                    >
                      {paymentProcessing ? 'Processing...' : 'Subscribe Now'}
                    </button>
                  </div>
                </div>
              </div>

              <div className="mt-6 text-center text-gray-400 text-sm">
                <p>💳 Secure payment powered by Stripe</p>
                <p>🔒 Cancel anytime • No long-term contracts</p>
              </div>
            </div>
          )}

          {/* Renew Subscription (for active subscribers nearing expiry) */}
          {subscription?.has_active_subscription && subscription?.days_remaining <= 7 && !subscription?.subscription_cancelled && subscription?.subscription_type !== 'recurring' && (
            <div className="bg-yellow-900/20 border border-yellow-600 rounded-lg p-6">
              <h2 className="text-2xl font-semibold mb-4 flex items-center">
                <span className="text-yellow-400 mr-3">⚠️</span>
                Renew Subscription
              </h2>
              <p className="text-gray-300 mb-4">
                Your subscription expires in {subscription.days_remaining} day{subscription.days_remaining !== 1 ? 's' : ''}. 
                Renew now to continue using the Dynamic Grid bot without interruption.
              </p>
              <button
                onClick={handleSubscribe}
                disabled={paymentProcessing}
                className="bg-yellow-600 hover:bg-yellow-700 disabled:bg-yellow-800 disabled:cursor-not-allowed px-6 py-3 rounded font-semibold transition-colors"
                data-testid="subscription-renew-button"
              >
                {paymentProcessing ? 'Processing...' : 'Upgrade to Auto-Renewing Subscription - $30/month'}
              </button>
            </div>
          )}

          {/* Auto-renewal notice for recurring subscribers */}
          {subscription?.has_active_subscription && subscription?.subscription_type === 'recurring' && !subscription?.subscription_cancelled && (
            <div className="bg-green-900/20 border border-green-600 rounded-lg p-6">
              <h2 className="text-xl font-semibold mb-2 flex items-center">
                <span className="text-green-400 mr-3">🔄</span>
                Auto-Renewal Active
              </h2>
              <p className="text-gray-300">
                Your subscription will automatically renew on {formatDate(subscription.next_billing_date || subscription.subscription_expires_at)}. 
                You&apos;ll be charged $30 to continue your access without interruption.
              </p>
            </div>
          )}

          {/* FAQ Section */}
          <div className="bg-gray-800 rounded-lg p-6">
            <h2 className="text-2xl font-semibold mb-4 flex items-center">
              <span className="text-purple-400 mr-3">❓</span>
              Frequently Asked Questions
            </h2>
            
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-300">How does the Dynamic Grid bot work?</h3>
                <p className="text-gray-400 mt-2">
                  The bot places a ladder of maker-only (post-only) limit orders above and below the current price on Hyperliquid. As the market oscillates, it captures the spread between fills and reduces taker fees. If your Hyperliquid account is in a maker-rebate tier, those rebates accrue too. Smart stops, a hard stop, and an automatic profit ratchet protect every basket.
                </p>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-300">Can I cancel anytime?</h3>
                <p className="text-gray-400 mt-2">
                  Yes. There are no long-term contracts. Cancel from this page at any time — your subscription remains active until the end of the current billing period, then stops automatically.
                </p>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-300">Is my money safe?</h3>
                <p className="text-gray-400 mt-2">
                  Your funds stay in your own Hyperliquid account. You generate an API wallet from Hyperliquid and paste only the trading key into actuvo — we can place and cancel orders but cannot withdraw funds. You can revoke the key from Hyperliquid at any time.
                </p>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-300">What is the minimum to start?</h3>
                <p className="text-gray-400 mt-2">
                  $20 of margin on Hyperliquid is enough to run a small grid. Most users start between $50 and $200 while they get comfortable with the controls.
                </p>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-300">What payment methods do you accept?</h3>
                <p className="text-gray-400 mt-2">
                  All major credit and debit cards, processed securely through Stripe. Hyperliquid trading itself runs on your own funded account — actuvo never touches your deposits.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      {showCancelConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4 border border-gray-700">
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.963-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-lg font-medium text-white">
                  Cancel Subscription
                </h3>
              </div>
            </div>
            <div className="mb-4">
              <p className="text-gray-300">
                Are you sure you want to cancel your subscription? You will retain access until the end of your current billing period.
              </p>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                className="px-4 py-2 text-sm font-medium text-gray-300 bg-gray-700 border border-gray-600 rounded-md hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                onClick={() => setShowCancelConfirm(false)}
              >
                Keep Subscription
              </button>
              <button
                type="button"
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                onClick={handleCancelConfirm}
                disabled={cancelLoading}
              >
                {cancelLoading ? 'Cancelling...' : 'Yes, Cancel Subscription'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionPage;